import { upsertStmCartItemsForCampaigns } from '@cart-services/hooks/use-stm-cart-store'
import { useStmInvalidateCartSessions } from '@cart-services/hooks/use-stm-invalidate-cart-sessions'
import { useStmSyncCartSessionStore } from '@cart-services/hooks/use-stm-sync-cart-session-to-store'
import { type ProjectCode } from '@constants'
import { useApiFastOrderCartSubmission } from '@hooks-api/fortune/use-api-fast-order-cart-submission'
import { FAST_ORDER_RETURN_TO_STEP, FAST_ORDER_SUBMISSION_ERROR_CODE, type FastOrderReturnToStepType, type FastOrderSubmissionRejected } from '@hooks-api/types/dataModel/fast-order'
import { ErrorFilled, SuccessFilled } from '@pets-ui/icons'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { AxiosError } from 'axios'
import { useRef } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { useButler } from '../../../../fp-b2b-shared/ai-purchase-order/components/ButlerProvider/useButler'
import { useActiveBrands } from '../../../hooks/use-active-brands'
import { useStmBrandStore } from '../../../hooks/use-stm-brand-store'
import { useStmMallCode } from '../../../hooks/use-stm-mall-code'
import { type CsvButlerItem, useStmQuickOrderStore } from '../../../hooks/use-stm-quick-order-store'
import { useFastOrderBrands } from '../../hooks/use-fast-order-brands'
import { toCsvItemChecksFromRejections, toFastOrderSubmissionItems, toStmCartItems } from '../../utils/fast-order-transformer'
import { groupCsvItemsByBrand } from './group-csv-items-by-brand'

const styles = stylex.create({
  snackbar: {
    backgroundColor: colors.grey800,
    color: colors.white,
    minWidth: '320px',
    width: '100%',
  },
})

interface UseCsvCartSubmissionArgs {
  onRejected: () => void
}

const isSubmissionRejected = (error: unknown): error is AxiosError<FastOrderSubmissionRejected> =>
  error instanceof AxiosError && error.response?.status === 409 && !!error.response.data?.error_code

const hasReturnToStep = (rejected: FastOrderSubmissionRejected, step: FastOrderReturnToStepType) =>
  rejected.errors?.some(error => error.return_to_step === step) ?? false

export const useCsvCartSubmission = ({ onRejected }: UseCsvCartSubmissionArgs) => {
  const history = useHistory()
  const mallCode = useStmMallCode()
  const { routes } = useButler()
  const activeBrands = useActiveBrands()
  const { fastOrderBrands } = useFastOrderBrands()
  const setBrand = useStmBrandStore(state => state.setBrand)
  const resetCsv = useStmQuickOrderStore(state => state.resetCsv)
  const setCsvItemChecks = useStmQuickOrderStore(state => state.setCsvItemChecks)
  const { invalidateCartSessions } = useStmInvalidateCartSessions()
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { trigger, isMutating } = useApiFastOrderCartSubmission({ mall_code: mallCode })
  // 同一份 payload 重送要沿用同一個 id 讓後端去重；payload 變了、成功或被退回都換新的
  const submissionRef = useRef<{ id: string, payloadKey: string } | null>(null)

  // 後端寫進 cart_request_cache 的只有 order_items，購物車頁的同步邏輯只認前端自己存的 cart_values，
  // 所以本機各品牌購物車要由前端寫入；購物車頁打 calculate 時會把完整 cart_values 回寫 session
  const writeBrandCarts = (items: CsvButlerItem[]) => {
    const campaignIdByProjectCode = new Map(activeBrands.map(brand => [brand.projectCode, brand.campaignId]))
    upsertStmCartItemsForCampaigns(groupCsvItemsByBrand(items).flatMap((group) => {
      const campaignId = campaignIdByProjectCode.get(group.projectCode)
      return campaignId ? [{ campaignId, items: toStmCartItems(group.items) }] : []
    }))
  }

  // 寫完本機後本機就是真相（這次沒提到的品牌是空車），所有啟用品牌都標成已同步，
  // 購物車頁才不會拉可能殘留的舊快照蓋掉本機；由各品牌頁的 calculate 把本機回寫 session
  const markAllBrandsSynced = () => {
    useStmSyncCartSessionStore.getState().addSyncedCampaignCodes(activeBrands.map(brand => brand.campaignCode))
  }

  const selectFirstBrand = (projectCodes: ProjectCode[]) => {
    const orderedProjectCodes = fastOrderBrands.length
      ? fastOrderBrands.map(brand => brand.projectCode).filter(projectCode => projectCodes.includes(projectCode))
      : projectCodes
    const firstBrand = activeBrands.find(brand => brand.projectCode === orderedProjectCodes[0])
    if (firstBrand) setBrand(firstBrand.campaignCode)
  }

  const returnToEntry = (message: string) => {
    submissionRef.current = null
    history.push(routes.entry)
    resetCsv()
    openSnackbar({ message, severity: 'error' })
  }

  // 後端每筆錯誤各自帶 return_to_step，整份清單取最嚴重的那一步：
  // entry（身分失效）> story_001（歸屬變更，需重新驗證）> story_002（品項要調整）> stay（暫時取不到資料，原地重試）
  const handleRejected = (rejected: FastOrderSubmissionRejected) => {
    if (rejected.error_code === FAST_ORDER_SUBMISSION_ERROR_CODE.SUBMISSION_IN_PROGRESS) {
      openSnackbar({ message: '加入採購處理中，請稍後再試', severity: 'error' })
      return
    }
    if (hasReturnToStep(rejected, FAST_ORDER_RETURN_TO_STEP.ENTRY)) {
      returnToEntry('登入狀態已失效，請重新登入後再試')
      return
    }
    if (hasReturnToStep(rejected, FAST_ORDER_RETURN_TO_STEP.STORY_001)) {
      returnToEntry('商品資料已變更，請重新上傳檔案')
      return
    }
    if (hasReturnToStep(rejected, FAST_ORDER_RETURN_TO_STEP.STORY_002)) {
      submissionRef.current = null
      const itemErrors = rejected.errors?.filter(error => error.return_to_step === FAST_ORDER_RETURN_TO_STEP.STORY_002) ?? []
      setCsvItemChecks(toCsvItemChecksFromRejections(itemErrors))
      onRejected()
      openSnackbar({ message: '部分商品狀態已變更，請重新確認後再加入', severity: 'error' })
      return
    }
    openSnackbar({ message: '暫時無法取得商品資料，請稍後再試', severity: 'error' })
  }

  const submit = async (items: CsvButlerItem[]) => {
    const submissionItems = toFastOrderSubmissionItems(items)
    const payloadKey = JSON.stringify(submissionItems)
    if (submissionRef.current?.payloadKey !== payloadKey) submissionRef.current = { id: crypto.randomUUID(), payloadKey }
    try {
      const { data: { data: result } } = await trigger({ submission_id: submissionRef.current.id, items: submissionItems })
      submissionRef.current = null
      await invalidateCartSessions()
      writeBrandCarts(items)
      markAllBrandsSynced()
      selectFirstBrand(result.brands.map(brand => brand.project_code))
      history.push(routes.cart)
      resetCsv()
      openSnackbar({ xStyles: { snackbar: styles.snackbar }, message: '已成功加入採購', icon: <SuccessFilled color={colors.green600} /> })
    }
    catch (error) {
      if (isSubmissionRejected(error) && error.response) {
        handleRejected(error.response.data)
        return
      }
      openSnackbar({ xStyles: { snackbar: styles.snackbar }, message: '加入採購失敗！發生未知的錯誤', icon: <ErrorFilled color={colors.red600} /> })
    }
  }

  return { submit, isSubmitting: isMutating }
}
